import React, { useState } from "react";
import { CgClose, CgSpinner } from "react-icons/cg";
import { GoCheckCircle, GoCheckCircleFill } from "react-icons/go";
import { BiSearch, BiFilter } from "react-icons/bi";
import {
  FaMapMarkerAlt,
  FaClock,
  FaBriefcase,
  FaDollarSign,
  FaCalendarAlt,
  FaUser,
} from "react-icons/fa";

import {
  ageBracket,
  jobAvailability,
  registrationDuration,
  salaryRange,
  sex_type,
  workType,
} from "./data";

import { lgaData } from "../../assets/localGov";

const AdvanceSearch = ({
  handler,
  state,
  setFormState,
  formState,
  submit,
  loading,
}) => {
  const [selectedDuration, setSelectedDuration] = useState(null);

  const handleRadioClick = (value) => {
    setSelectedDuration(value);
    setFormState((prev) => ({ ...prev, registrationDuration: value }));
  };

  const handleSelectChange = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const SelectField = ({ label, icon: Icon, options, field, placeholder }) => (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-gray-700 font-medium">
        <Icon className="text-blue-500 text-lg" />
        <label className="text-sm">{label}</label>
      </div>
      <select
        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:bg-gray-100"
        value={formState[field] || ""}
        onChange={(e) => handleSelectChange(field, e.target.value)}
      >
        <option value="">{placeholder}</option>
        {options?.map((option, index) => {
          const value = typeof option === "object" ? option.value : option;
          const display =
            typeof option === "object"
              ? option.title
              : option.includes("_")
              ? option.split("_").join(" ")
              : option;
          return (
            <option key={index} className="capitalize" value={value}>
              {display}
            </option>
          );
        })}
      </select>
    </div>
  );

  const localGov = lgaData[state];

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-8 mt-5 relative border border-gray-100 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
            <BiFilter className="text-white text-xl" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              Advanced Search
            </h2>
            <p className="text-gray-500 text-sm">
              Find the perfect candidate with detailed filters
            </p>
          </div>
        </div>
        <button
          onClick={handler}
          className="p-2 hover:bg-red-50 rounded-xl transition-colors duration-200 group"
        >
          <CgClose className="text-red-500 group-hover:text-red-600 text-2xl" />
        </button>
      </div>

      <div className="space-y-8">
        {/* Location Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <FaMapMarkerAlt className="text-blue-500" />
            Location Details
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <SelectField
              label="Local Government"
              icon={FaMapMarkerAlt}
              options={localGov}
              field="localGovernment"
              placeholder="Choose Local Government"
            />
          </div>
        </div>

        {/* Registration Timeline */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <FaClock className="text-green-500" />
            Registration Timeline
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {registrationDuration.map((timeFrame) => {
              const value = timeFrame.toLowerCase();
              const isSelected = selectedDuration === value;

              return (
                <div
                  key={value}
                  className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all duration-200 border-2 ${
                    isSelected
                      ? "bg-green-100 border-green-300 shadow-md transform scale-105"
                      : "bg-white border-gray-200 hover:border-green-200 hover:bg-green-50"
                  }`}
                  onClick={() => handleRadioClick(value)}
                >
                  <span
                    className={`text-sm font-medium ${
                      isSelected ? "text-green-700" : "text-gray-700"
                    }`}
                  >
                    {timeFrame}
                  </span>
                  {isSelected ? (
                    <GoCheckCircleFill className="text-green-500 text-xl" />
                  ) : (
                    <GoCheckCircle className="text-gray-400 text-xl" />
                  )}
                </div>
              );
            })}
            <input type="date" />
          </div>
        </div>

        {/* Work Details Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <SelectField
            label="Work Type"
            icon={FaBriefcase}
            options={workType}
            field="workType"
            placeholder="Choose work type"
          />

          <SelectField
            label="Salary Range"
            icon={FaDollarSign}
            options={salaryRange}
            field="salary"
            placeholder="Select salary range"
          />

          <SelectField
            label="Availability"
            icon={FaCalendarAlt}
            options={jobAvailability}
            field="availability"
            placeholder="Choose availability"
          />
        </div>

        {/* Personal Details Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <SelectField
            label="Age Bracket"
            icon={FaUser}
            options={ageBracket}
            field="age"
            placeholder="Select age range"
          />

          <SelectField
            label="Gender"
            icon={FaUser}
            options={sex_type}
            field="gender"
            placeholder="Select gender"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
          <button
            type="submit"
            className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-4 px-6 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            onClick={submit}
          >
            {loading ? (
              <CgSpinner className="animate-spin" />
            ) : (
              <div className="flex items-center justify-center">
                <BiSearch className="text-xl" />
                Search Candidates
              </div>
            )}
          </button>
          <button
            type="button"
            className="flex-1 sm:flex-initial bg-gray-100 text-gray-700 py-4 px-6 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-200 min-w-[140px]"
            onClick={() => {
              setFormState({});
              setSelectedDuration(null);
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvanceSearch;
